
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FileUploadMenu from './FileUploadMenu';

interface FileUploadPortalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFileType: (fileType: any) => void;
  attachmentButtonRef: React.RefObject<HTMLButtonElement>;
  isMobile?: boolean;
}

export default function FileUploadPortal({ 
  isOpen, 
  onClose, 
  onSelectFileType,
  attachmentButtonRef,
  isMobile = false
}: FileUploadPortalProps) {
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  
  // Calculate menu position based on attachment button position
  useEffect(() => {
    if (isOpen && attachmentButtonRef.current) {
      const rect = attachmentButtonRef.current.getBoundingClientRect();
      const menuHeight = 230; // Height of the menu
      
      if (isMobile) {
        // On mobile, center the menu horizontally and position it higher above the button
        const verticalOffset = Math.min(180, rect.top - 20); // More aggressive offset for mobile
        const windowWidth = window.innerWidth;
        const menuWidth = 280; // Width of the menu in px
        
        // Center horizontally but ensure it stays within screen bounds
        const leftPosition = Math.max(16, Math.min(windowWidth - menuWidth - 16, windowWidth / 2 - menuWidth / 2));
        
        setMenuPosition({
          top: Math.max(16, rect.top - menuHeight - verticalOffset),
          left: leftPosition,
        });
      } else {
        // Desktop positioning
        // Calculate horizontal position
        // The attachment button is typically 36px wide, and we want the triangle pointer
        // (positioned at left: 130px in the menu) to align with the center of the button
        const buttonCenter = rect.left + (rect.width / 2);
        const menuLeft = buttonCenter - 130; // 130px is where the triangle pointer is
        
        // Set a larger fixed vertical offset to position well above input field
        // We'll use 100px as the minimum distance from the top of the button
        const verticalOffset = 100;
        
        setMenuPosition({
          top: Math.max(10, rect.top - menuHeight - verticalOffset),
          left: Math.max(20, menuLeft),
        });
      }
    }
  }, [isOpen, attachmentButtonRef, isMobile]);

  // Only render when menu is open
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[999] pointer-events-none"
      onClick={onClose}
    >
      <div 
        className={`pointer-events-auto absolute file-upload-portal ${isMobile ? 'mobile-file-upload-menu' : ''}`}
        style={{ 
          top: menuPosition.top, 
          left: menuPosition.left,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", damping: 25 }}
          >
            {/* Visual elements for connection to button - Only on desktop */}
            {!isMobile && (
              <div className="file-upload-line absolute w-[1px] bg-gray-200/50 z-[-1]" style={{
                height: `${menuPosition.top ? window.innerHeight - menuPosition.top : 0}px`,
                bottom: '-100vh',
                left: '130px',
              }}></div>
            )}
            
            {/* Triangle pointers - Only on desktop */}
            {!isMobile && (
              <>
                <div className="file-upload-triangle"></div>
                <div className="file-upload-triangle-shadow"></div>
              </>
            )}
            
            <FileUploadMenu 
              isOpen={isOpen} 
              onClose={onClose}
              onSelectFileType={onSelectFileType}
              isMobile={isMobile}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
